import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Avatar, Button, Form, Input, Select, Space} from 'antd';
import {connect} from 'react-redux';
import {Collapse} from 'react-collapse';
import {useNavigate} from 'react-router-dom';
import {
  StyledCardDetailsPanel,
  StyledCardDetailsPanelCard,
  StyledCardDetailsPanelInput,
} from './StyledCardDetailsPanel';
import {
  StyledBudgetDetailsFormSpace
} from '../../SubscriptionPage/tabComponents/Overview/BudgetDetails/StyledBudgetDetails';
import {StyledCardPaymentTypeCardIconSpace} from '../../CardsPage/CardPaymentType/StyledCardPaymentType';
import EditButton from '../../../EditButton';
import SpinSmall from '../../../SpinSmall';
import IncreaseLimitAlert from '../../SubscriptionsPage/IncreaseLimitAlert';
import DetailsTable from '../../TransactionsPage/DetailsTable';
import CardDetails from '../../SubscriptionPage/tabComponents/Overview/CardDetails';
import CardBanner from '../../CardsPage/CardBanner';
import HardCardLimitSwitch from '../../SubscriptionsPage/HardCardLimitSwitch';
import SubscriptionIconWithLink from '../../SubscriptionPage/SubscriptionIconWithLink';
import {EuroIcon, WithLessCreditCardIcon} from '../../../../icons';
import {
  amountHelpers,
  cardsHelpers,
  BillOptions,
  CardLimitPeriodTypeOptions,
  formHelpers,
  objectHelpers,
  subscriptionsHelpers,
  textHelpers
} from '../../../../utils/helpers';
import {
  subscriptionFormFields,
  subscriptionFormValues,
  cardBackendStatusesConstants,
  cardStatusesConstants
} from '../../../../constants';
import routes from '../../../../routes/routes.json';
import {useHasAccess} from '../../../../hooks';
import {alertActions} from '../../../../state/actions/alert.actions';

const {
  budgetLimitFieldName,
  billedFieldName,
  cardLimitFieldName,
  limitPeriodShortFieldName
} = subscriptionFormFields;

const {
  defaultBilled,
  defaultCardLimitPeriod
} = subscriptionFormValues;

const {getAmountWithCurrencyCode} = amountHelpers;
const {getObjProp: gObjProp} = objectHelpers;


const {getCardLimitTypeFromBilled} = cardsHelpers;

const {Item} = Form;

const CardDetailsPanel = ({
  cardDetails,
  cardHold,
  cardHoldMessage,
  cardLimits,
  className,
  dispatch,
  costCenterTags,
  isAdmin,
  employeeId,
  onActivate,
  onFormSubmit,
  onPauseSuccess,
  onSetPinClick,
  onUnblock,
  isOpenAuthModal = false,
  subscription,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'cards', 'subscriptions']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState({
    [billedFieldName]: defaultBilled,
    [budgetLimitFieldName]: '',
    [cardLimitFieldName]: '',
    [limitPeriodShortFieldName]: defaultCardLimitPeriod,
    cost_centers: [],
    name: ''
  });
  const [switchProps, setSwitchProps] = useState({checked: false, loading: false});
  const [billOptions, ] = useState(BillOptions());
  const [cardLimitPeriodTypeOptions] = useState(CardLimitPeriodTypeOptions());
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isUnblockLoading, setIsUnblockLoading] = useState(false);
  const [isSelectedHardLimit, setIsSelectedHardLimit] = useState(false);

  const navigate = useNavigate();

  const isForPersonalUseCard = useMemo(() => objectHelpers.isNaV(cardDetails?.cardData?.subscription_id), [cardDetails]);

  const tagOptions = useMemo(() => formHelpers.getTagOptions(costCenterTags), [costCenterTags]); // eslint-disable-line react-hooks/exhaustive-deps

  const trans = (key) => t(`cards:${key}`);

  const tooltipT = (key) => trans(`tooltips.${key}`);

  const gDataProp = (subscription, key, defaultValue) => gObjProp(subscription, key, defaultValue);

  const gCardProp = (key) => gDataProp(cardDetails.cardData, key);

  const cardId = useMemo(() => cardDetails?.card_id || null, [cardDetails.card_id]);

  const cardOwner = useMemo(() => cardDetails?.cardData?.owner || null, [cardDetails?.cardData?.owner]);

  const billedLimitPeriod = Form.useWatch(billedFieldName, {form});
  const budgetLimit = Form.useWatch(budgetLimitFieldName, {form});
  const cardLimit = Form.useWatch(cardLimitFieldName, {form});

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}];
  const hasEditAccess = useHasAccess('PUT_UPDATE_CARD');

  const finishUnblockLoading = () => setIsUnblockLoading(false);

  const {
    enableChangeCardAction,
    isCardError,
    isKnownStatusCode,
    isPending,
    isTerminatedCard
  } = useMemo(() => {
    const isKnownStatusCode = cardDetails?.bankingData?.status_code !== undefined;

    // enable edit if card status is not deleted/terminated/restricted/unmanaged
    const statusCode = isKnownStatusCode ? cardsHelpers.getStatus(cardDetails.bankingData) : undefined;
    const isPending = isKnownStatusCode && statusCode === cardStatusesConstants.PENDING;
    const isCardError = isKnownStatusCode && statusCode === cardStatusesConstants.CARD_ERROR;
    const isTerminatedCard = isKnownStatusCode && statusCode === cardStatusesConstants.TERMINATED;
    const enableChangeCardAction = [cardStatusesConstants.ACTIVE, cardStatusesConstants.FROZEN].includes(statusCode);
    return {
      enableChangeCardAction,
      isCardError,
      isKnownStatusCode,
      isPending,
      isTerminatedCard
    }
  }, [cardDetails.bankingData]);

  const isHoldCard = useMemo(() => cardHold || (cardId === null && isPending), [cardHold, cardId, isPending]);

  const isEnabledEdit = useMemo(() => hasEditAccess && !isHoldCard && isAdmin, [hasEditAccess, isHoldCard, isAdmin]);

  const isEnableSetPin = useMemo(() => {
    const {
      is_live: isLive = true,
      pin_try_exceeds: isPinLocked,
      status_code: statusCode,
      user_id: userId,
      virtual_converted: isVirtualConverted = false
    } = cardDetails?.bankingData || {};

    // If the card is not virtual, we don't allow setting a pin
    if (!isVirtualConverted) return false;

    const isCardUnlocked = statusCode === cardBackendStatusesConstants.UNLOCK;
    const isCardOwner = employeeId === userId;

    return isCardOwner && isLive && !isPinLocked && isCardUnlocked;
  }, [cardDetails?.bankingData, employeeId]);

  const isEnableWalletBanner = useMemo(() => {
    let enable = false;
    if (isKnownStatusCode) {
      enable = isForPersonalUseCard ? !cardsHelpers.isDigitalizedCard(cardDetails?.cardData) : false;
    }
    return enable;
  }, [cardDetails?.cardData, isForPersonalUseCard, isKnownStatusCode]);

  const personalCardLimit = useMemo(() => {
    return cardLimits[isForPersonalUseCard ? 'personalCardLimit' : 'subscriptionCardLimit'] || 10000
  }, [cardLimits, isForPersonalUseCard]);

  const isTooHighLimit = useMemo(() => {
    let isHigh = false;
    if (isEditMode && cardLimit) {
      isHigh = cardsHelpers.isTooHighLimit({
        limit: cardLimit,
        maxLimit: personalCardLimit
      });
    }
    return isHigh;
  }, [isEditMode, cardLimit, personalCardLimit]);

  const error = useMemo(() => cardId === null && isCardError && !isHoldCard, [cardId, isCardError, isHoldCard]);

  const renderActionButton = (text, onClick, loading = false) => (
    <div className='d-flex justify-end'>
      <Button
        loading={loading}
        onClick={onClick}
        type='primary'
      >
        {text}
      </Button>
    </div>
  );

  const handleUnblockCard = () => {
    if (onUnblock) {
      setIsUnblockLoading(true);
      onUnblock({
        errorCallback: finishUnblockLoading,
        successCallback: finishUnblockLoading,
      })
    }
  }

  const creditCardProps = useMemo(() => {
    const bankingData = cardDetails?.bankingData ?? {};

    const {
      is_live: isLive = true,
      pin_try_exceeds: isPinLocked,
      virtual_converted: isVirtualConverted,
      user_id: userId
    } = bankingData;

    const isCardOwner = employeeId === userId;

    // Determine if the physical card is active (defaults true if no virtual conversion)
    const isCardActive = isVirtualConverted ? isLive : true;

    // Card is on hold if physical card is active and isHoldCard flag is true, otherwise true (if not physical active)
    const isCardOnHold = isCardActive ? isHoldCard : true;

    let actions = null;
    let errorMessage = cardHoldMessage;
    let showPlaceholder = false;

    if (!isCardActive) {
      errorMessage = t('cards:errors.activateYourCard');
      showPlaceholder = true;

      if (isCardOwner) {
        actions = renderActionButton(t('activate'), onActivate);
      }

    } else if (isPinLocked) {
      errorMessage = t('cards:errors.unblockCard');
      showPlaceholder = true;

      if (isCardOwner) {
        actions = renderActionButton(
          t('unblock'),
          handleUnblockCard,
          isUnblockLoading
        );
      }
    }

    return {
      actions,
      error,
      errorMessage,
      hold: isCardOnHold,
      showPlaceholder,
      variant: cardsHelpers.getCardType(bankingData),
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    cardDetails?.bankingData,
    cardHoldMessage,
    error,
    employeeId,
    isHoldCard,
    isUnblockLoading,
  ]);

  useEffect(() => {
    if (isSelectedHardLimit && isEditMode && budgetLimit !== cardLimit) {
      form.setFieldValue(cardLimitFieldName, budgetLimit);
    }
  }, [budgetLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSelectedHardLimit && isEditMode) {
      form.setFieldValue(limitPeriodShortFieldName, cardsHelpers.getCardLimitTypeFromBilled(billedLimitPeriod));
    }
  }, [billedLimitPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabledSaveButton = useMemo(() => isTooHighLimit || isFormLoading, [isFormLoading, isTooHighLimit]);

  useEffect(() => {
    const gCardProp = (key) => gDataProp(cardDetails.cardData, key);
    const fieldValues = {
      ...initialFormValues,
      [billedFieldName]: gCardProp(billedFieldName),
      [budgetLimitFieldName]: gCardProp(budgetLimitFieldName),
      [cardLimitFieldName]: gCardProp(cardLimitFieldName),
      [limitPeriodShortFieldName]: gCardProp(limitPeriodShortFieldName),
      cost_centers: gCardProp('cost_centers') || [],
      name: gCardProp('name')
    };
    const isSelectedHardLimit = cardsHelpers.isHardLimitSelected(cardDetails.cardData);
    setInitialFormValues(fieldValues);
    form.setFieldsValue(fieldValues);
    setIsSelectedHardLimit(isSelectedHardLimit);
    isEditMode && setIsEditMode(false);
  }, [cardDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSwitchProps({
      checked: gDataProp(cardDetails?.cardData, 'lock_status') === cardStatusesConstants.ACTIVE,
      loading: false
    });
  }, [cardDetails]);

  useEffect(() => {
    if (!isOpenAuthModal && switchProps.loading) setSwitchProps({...switchProps, loading: false});
  }, [isOpenAuthModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardOwnerAvatarData = {
    label: gObjProp(cardOwner, 'full_name'),
    src: gObjProp(cardOwner, 'logo') || undefined
  }

  const getTableData = () => {
    const onlyAdminAvailableFields = ['active', 'budget', 'owner'];
    let data = [
      {
        key: 'cardName',
        label: trans('cardName'),
        value: gCardProp('name')
      },
      {
        key: 'cardType',
        label: trans('cardType'),
        value: (
          <StyledCardPaymentTypeCardIconSpace
            className={cardsHelpers.getCardIconClassName(cardDetails?.cardData)}
            align='center'
            size='small'
          >
            {t(`cardTypes.${isForPersonalUseCard ? 'forPersonalUse' : 'forSubscriptions'}`)}
            <WithLessCreditCardIcon />
          </StyledCardPaymentTypeCardIconSpace>
        )
      },
      {
        key: 'publicToken',
        label: trans('publicToken'),
        value: cardDetails?.bankingData?.public_token || ''
      },
      {
        key: 'owner',
        label: `${t('card')} ${t('Owner')}`,
        value: (
          <Space>
            {cardOwnerAvatarData.label}
            <Avatar src={cardOwnerAvatarData.src}>
              {textHelpers.getInitials(cardOwnerAvatarData.label)}
            </Avatar>
          </Space>
        )
      },
      !isForPersonalUseCard && {
        key: 'subscription',
        label: t('subscriptions:subscription'),
        value: <SubscriptionIconWithLink subscription={subscription} />
      },
      {
        key: 'budget',
        label: t('Budget'),
        value: subscriptionsHelpers.getBudgetBilledValue({t, data: cardDetails.cardData})
      },
      {
        key: 'costCenter',
        label: t('costCenter'),
        value: (gCardProp('cost_centers') || []).join(', ')
      },
      {
        key: 'cardLimit',
        label: t('subscriptions:cardLimit'),
        value: <>{getAmountWithCurrencyCode(gCardProp(cardLimitFieldName))} {t(subscriptionsHelpers.getCardLimitType(gCardProp('limit_period')))}</>
      },
      isEnableSetPin && {
        key: 'setPin',
        value: (
          <Button
            onClick={onSetPinClick}
            type='primary'
          >
            {t('setNewPin')}
          </Button>
        )
      }
    ].filter(d => d);
    if (!isAdmin) data = data.filter(i => !onlyAdminAvailableFields.includes(i.key));
    return data;
  }

  const handleEdit = () => enableChangeCardAction && setIsEditMode(true);

  const handleOnCancel = () => {
    setIsEditMode(false);
    setIsFormLoading(false);
  }

  const handleOnSave = () => !disabledSaveButton && form.submit();

  const handleSubmit = (data) => {
    if (onFormSubmit) {
      setIsFormLoading(true);
      onFormSubmit({
        data,
        successCallback: handleOnCancel,
        errorCallback: () => setIsFormLoading(false)
      });
    } else {
      handleOnCancel();
    }
  }

  const handleChangeHardLimit = (value) => {
    setIsSelectedHardLimit(value);
    if (value) {
      form.setFieldsValue({
        [cardLimitFieldName]: budgetLimit,
        [limitPeriodShortFieldName]: getCardLimitTypeFromBilled(billedLimitPeriod)
      })
    }
  }

  const handleDeleteSuccess = () => {
    alertActions.success(t('cards:successCardDeleteMessage'));
    navigate(routes.cardsList);
  }

  const tableData = getTableData();

  const extra = (!error && isEnabledEdit) && (
    <>
      {isEditMode ? (
        <Space size='middle'>
          <Button
            onClick={handleOnCancel}
          >
            {t('cancel')}
          </Button>
          <Button
            disabled={disabledSaveButton}
            onClick={handleOnSave}
            type='primary'
          >
            {t('save')}
          </Button>
        </Space>
      ) : (
        <EditButton
          onClick={handleEdit}
        />
      )}
    </>
  );

  const isOpenedDetailsTable = isEditMode ? false : !isEditMode && (!isTerminatedCard);

  const budgetFieldRules = subscriptionsHelpers.getBudgetFieldRules(t);

  return (
    <StyledCardDetailsPanel
      {...rest}
    >
      <StyledCardDetailsPanelCard
        extra={extra}
        title={t('cardDetail')}
      >
        <SpinSmall spinning={cardDetails.loading}>
          <CardDetails
            className='card-details'
            cardDetails={cardDetails.bankingData}
            cardId={cardDetails?.cardData?.card_id}
            creditCardProps={creditCardProps}
            onDeleteSuccess={handleDeleteSuccess}
            onPauseSuccess={onPauseSuccess}
          />
        </SpinSmall>

        <Collapse isOpened={isOpenedDetailsTable}>
          {isEnableWalletBanner && <CardBanner />}
          <DetailsTable data={tableData}/>
        </Collapse>

        <Collapse isOpened={isEditMode}>
          <SpinSmall spinning={isFormLoading}>
            <Form
              className='pt-20'
              initialValues={initialFormValues}
              form={form}
              layout='vertical'
              onFinish={handleSubmit}
              requiredMark={false}
            >
              <Item
                label={trans('cardName')}
                name='name'
                required
              >
                <Input size='large' />
              </Item>
              <Item
                label={t('costCenter')}
                name='cost_centers'
              >
                <Select
                  size='large'
                  options={tagOptions}
                  mode='multiple'
                />
              </Item>
              <StyledBudgetDetailsFormSpace
                align='start'
                size='middle'
              >
                <Item
                  label={t('Budget')}
                  name={budgetLimitFieldName}
                  rules={budgetFieldRules}
                  required
                >
                  <StyledCardDetailsPanelInput
                    addonBefore={<EuroIcon />}
                    size='large'
                    type='number'
                  />
                </Item>
                <Item
                  label=' '
                  name={billedFieldName}
                  rules={requiredRules}
                >
                  <Select
                    options={billOptions}
                    size='large'
                  />
                </Item>
              </StyledBudgetDetailsFormSpace>
              <Item>
                <HardCardLimitSwitch
                  checked={isSelectedHardLimit}
                  onChange={handleChangeHardLimit}
                  tooltipProps={{
                    title: <>{tooltipT('hardCardLimit1')}<br/>{tooltipT('hardCardLimit2')}</>
                  }}
                />
              </Item>
              <StyledBudgetDetailsFormSpace
                align='start'
                size='middle'
              >
                <Item
                  label={`${t('card')} ${t('limit')}`}
                  name={cardLimitFieldName}
                  rules={budgetFieldRules}
                  required
                >
                  <StyledCardDetailsPanelInput
                    addonBefore={<EuroIcon />}
                    disabled={isSelectedHardLimit}
                    size='large'
                    type='number'
                  />
                </Item>
                <Item
                  label=' '
                  name={limitPeriodShortFieldName}
                  rules={requiredRules}
                >
                  <Select
                    disabled={isSelectedHardLimit}
                    options={cardLimitPeriodTypeOptions}
                    size='large'
                  />
                </Item>
              </StyledBudgetDetailsFormSpace>
              <Collapse isOpened={isTooHighLimit}>
                <IncreaseLimitAlert
                  limit={personalCardLimit}
                />
              </Collapse>
            </Form>
          </SpinSmall>
        </Collapse>
      </StyledCardDetailsPanelCard>
    </StyledCardDetailsPanel>
  );
}

CardDetailsPanel.propTypes = {
  cardDetails: PropTypes.shape({
    bankingData: PropTypes.object,
    cardData: PropTypes.object,
    loading: PropTypes.bool
  }),
  onActivate: PropTypes.func,
  onFormSubmit: PropTypes.func,
  onPauseSuccess: PropTypes.func,
  onSetPinClick: PropTypes.func,
  onUnblock: PropTypes.func,
  subscription: PropTypes.object,
  isOpenAuthModal: PropTypes.bool
}

const mapStateToProps = state => {
  const {isAdmin} = state.user;
  const {id: employeeId} = state.user.employee;
  const {costCenterTags} = state.company;
  const cardLimits = cardsHelpers.getPaymentCardLimit(state);
  return {
    cardLimits,
    costCenterTags,
    employeeId,
    isAdmin,
  }
}

export default connect(mapStateToProps, null)(CardDetailsPanel);
